import { useState, useEffect, useRef } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, X, Check, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

interface CpvCode {
  code: string;
  description: string;
  category: string;
  division: string | null;
}

interface CpvSearchProps {
  selectedCodes: string[];
  onCodesChange: (codes: string[]) => void;
  availableCodes?: string[];
}

export default function CpvSearch({ selectedCodes, onCodesChange, availableCodes }: CpvSearchProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CpvCode[]>([]);
  const [displayResults, setDisplayResults] = useState<CpvCode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Search CPV codes from database using translated descriptions
  useEffect(() => {
    const searchCpvCodes = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const currentLang = i18n.language?.substring(0, 2) || 'en';
        
        // Search by code directly in cpv_codes
        const codeSearch = searchQuery.match(/^\d/)
          ? supabase
              .from('cpv_codes')
              .select('code, description, category, division')
              .ilike('code', `%${searchQuery}%`)
              .limit(20)
          : null;

        // Search translations in current language
        const { data: translationHits, error: trError } = await supabase
          .from('cpv_translations')
          .select('code, description')
          .eq('language', currentLang)
          .ilike('description', `%${searchQuery}%`)
          .limit(20);

        if (trError) console.error('Translation search error:', trError);

        const matchedCodes = new Set((translationHits || []).map(t => t.code));
        const translationMap = new Map((translationHits || []).map(t => [t.code, t.description]));

        // Also get code-based matches
        let codeMatchCodes: string[] = [];
        if (codeSearch) {
          const { data: codeData } = await codeSearch;
          (codeData || []).forEach(c => {
            if (!matchedCodes.has(c.code)) {
              matchedCodes.add(c.code);
            }
          });
          codeMatchCodes = (codeData || []).map(c => c.code);
        }

        if (matchedCodes.size === 0) {
          // Fallback: search cpv_codes English descriptions
          const { data: fallback } = await supabase
            .from('cpv_codes')
            .select('code, description, category, division')
            .or(`description.ilike.%${searchQuery}%,category.ilike.%${searchQuery}%`)
            .limit(20);
          const results = fallback || [];
          setSearchResults(results);
          setDisplayResults(results);
          setIsLoading(false);
          return;
        }

        // Fetch full cpv_codes data for matched codes
        const allCodes = Array.from(matchedCodes);
        const { data: cpvData, error: cpvError } = await supabase
          .from('cpv_codes')
          .select('code, description, category, division')
          .in('code', allCodes)
          .limit(20);

        if (cpvError) throw cpvError;

        // Override descriptions with translated ones
        const results: CpvCode[] = (cpvData || []).map(cpv => ({
          ...cpv,
          description: translationMap.get(cpv.code) || cpv.description,
        }));

        setSearchResults(results);
        setDisplayResults(results);
      } catch (error) {
        console.error('Error searching CPV codes:', error);
        setSearchResults([]);
        setDisplayResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchCpvCodes, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
        setDisplayResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleCode = (code: string) => {
    if (selectedCodes.includes(code)) {
      onCodesChange(selectedCodes.filter(c => c !== code));
    } else {
      onCodesChange([...selectedCodes, code]);
    }
    setSearchQuery('');
    // Keep displayResults so dropdown stays open for multi-select
    inputRef.current?.focus();
  };

  const removeCode = (code: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    onCodesChange(selectedCodes.filter(c => c !== code));
  };

  const isCodeAvailable = (code: string) => {
    if (!availableCodes) return true;
    return availableCodes.some(ac => ac.startsWith(code.substring(0, 2)));
  };

  const showDropdown = isFocused && (displayResults.length > 0 || (searchQuery.length >= 2 && isLoading));

  return (
    <div ref={containerRef} className="relative">
      {/* Token input container - fixed height with horizontal scroll */}
      <div 
        className="flex items-center h-9 border rounded-md bg-background px-2 gap-1 cursor-text overflow-x-auto scrollbar-thin"
        onClick={() => inputRef.current?.focus()}
      >
        <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        
        {/* Selected tokens */}
        {selectedCodes.map(code => (
          <span 
            key={code}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs bg-primary text-primary-foreground rounded flex-shrink-0 whitespace-nowrap"
          >
            {code}
            <X 
              className="w-3 h-3 cursor-pointer hover:opacity-70" 
              onClick={(e) => removeCode(code, e)}
            />
          </span>
        ))}
        
        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          placeholder={selectedCodes.length === 0 ? t('cpv.searchPlaceholder') : ""}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          className="flex-1 min-w-[60px] bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground"
        />
        
        {isLoading && (
          <Loader2 className="w-4 h-4 text-muted-foreground animate-spin flex-shrink-0" />
        )}
      </div>

      {/* Search results dropdown */}
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 border rounded-lg bg-popover shadow-lg">
          {displayResults.length > 0 ? (
            <ScrollArea className="h-48">
              <div className="p-2 space-y-1">
                {displayResults.map(cpv => {
                  const isSelected = selectedCodes.includes(cpv.code);
                  const available = isCodeAvailable(cpv.code);
                  
                  return (
                    <div
                      key={cpv.code}
                      className={cn(
                        "flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors",
                        isSelected 
                          ? "bg-primary/10 text-primary" 
                          : "hover:bg-muted",
                        !available && "opacity-50"
                      )}
                      onClick={() => toggleCode(cpv.code)}
                    >
                      <div className={cn(
                        "w-5 h-5 rounded border flex items-center justify-center flex-shrink-0",
                        isSelected 
                          ? "bg-primary border-primary text-primary-foreground" 
                          : "border-muted-foreground/30"
                      )}>
                        {isSelected && <Check className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-medium">{cpv.code}</span>
                          <span className="text-xs text-muted-foreground bg-muted px-1 rounded">
                            {cpv.category}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {cpv.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : !isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-3">
              {t('cpv.noResults')}
            </p>
          ) : null}
        </div>
      )}

      {/* Show "no results" only when focused and typed but no results */}
      {isFocused && searchQuery.length >= 2 && !isLoading && searchResults.length === 0 && displayResults.length === 0 && (
        <div className="absolute z-50 w-full mt-1 border rounded-lg bg-popover shadow-lg">
          <p className="text-sm text-muted-foreground text-center py-3">
            {t('cpv.noResults')}
          </p>
        </div>
      )}
    </div>
  );
}
